import { useState } from "react";

import { Button, Modal, Table } from "react-bootstrap";

import OverviewModal from "../overview/overview-modal/overview-modal";
import ItemModal from "./item-modal";
import ModuleModal from "./module-modal";
import SurveyModal from "./survey-modal";

type Survey = {
  id: number | null;
  survey_type: number;
  study: number;
  survey: number | null;
  module: number | null;
  title: string;
  year: number;
  studyName: string;
};

type QuestionnaireTableProps = {
  headers: string[];
  data: {
    [key: string]: {
      id: number;
      has_overview: boolean;
      years: {
        [year: string]: Survey[];
      };
    };
  };
};

type ItemProps = {
  id: number;
  title: string;
  description: string;
  item_type: {
    title: string;
  };
};

export default function QuestionnaireTable({ headers, data }: QuestionnaireTableProps) {
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyId, setSurveyId] = useState<number | undefined>();

  const [showModuleModal, setShowModuleModal] = useState(false);
  const [moduleId, setModuleId] = useState<number>();

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemId, setItemId] = useState<number>();
  const [itemIndex, setItemIndex] = useState(0);
  const [items, setItems] = useState<ItemProps[]>([]);

  const [showStudyModal, setShowStudyModal] = useState(false);
  const [studyId, setStudyId] = useState<number>();
  const [studyTitle, setStudyTitle] = useState<string>();

  const handleOpenSurveyModal = (surveyId: number | undefined) => {
    setSurveyId(surveyId);
    setShowSurveyModal(true);
  };

  const handleCloseSurveyModal = () => {
    setShowSurveyModal(false);
  };

  const handleOpenModuleModal = (moduleId: number) => {
    setModuleId(moduleId);
    setShowModuleModal(true);
  };

  const handleCloseModuleModal = () => {
    setShowModuleModal(false);
  };

  const handleOpenItemModal = (itemId: number, items: ItemProps[], currentIndex: number) => {
    setItemId(itemId);
    setItemIndex(currentIndex);
    setItems(items);
    setShowItemModal(true);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
  };

  const handleOpenStudyModal = (studyId: number, studyTitle: string) => {
    setStudyId(studyId);
    setStudyTitle(studyTitle);
    setShowSurveyModal(false);
    setShowModuleModal(false);
    setShowItemModal(false);
    setShowStudyModal(true);
  };

  return (
    <>
      <Table striped responsive className="mt-4">
        <thead>
          <tr className="g2-table-header-blue text-center">
            {headers.map((item, index) => (
              <th key={index} className={`${index == 0 ? `text-start` : `text-center`}`}>
                {item}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([studyName, studyData], studyIndex) => (
            <tr key={studyIndex} className="align-top text-center">
              {studyData.has_overview ? (
                <td
                  className="text-start g2-link-blue fw-bold"
                  onClick={() => handleOpenStudyModal(studyData.id, studyName)}
                >
                  {studyName}
                </td>
              ) : (
                <td className="text-start">
                  <span className="g2-text-grey fw-bold">{studyName}</span>
                </td>
              )}
              {headers.slice(1).map((header, headerIndex) => (
                <td key={headerIndex}>
                  {studyData.years[header] ? (
                    studyData.years[header].map((survey, surveyIndex, surveyArray) => (
                      <div
                        key={surveyIndex}
                        onClick={() => {
                          if (survey.survey !== null && survey.id !== null) {
                            handleOpenSurveyModal(survey.id);
                          } else if (survey.module !== null) {
                            handleOpenModuleModal(survey.module);
                          }
                        }}
                      >
                        <span className="g2-link-blue text-decoration-none">{survey.title}</span>
                        {surveyIndex !== surveyArray.length - 1 && <hr className="my-2 mx-auto w-75" />}
                      </div>
                    ))
                  ) : (
                    <span></span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal
        show={showStudyModal}
        onHide={() => setShowStudyModal(false)}
        size="xl"
        aria-labelledby="srv-overview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="g2-text-grey">{studyTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{studyId !== undefined && <OverviewModal studyId={studyId} cohort_name={undefined} />}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStudyModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {showSurveyModal && (
        <SurveyModal
          showSurveyModal={showSurveyModal}
          surveyId={surveyId}
          handleCloseSurveyModal={handleCloseSurveyModal}
          handleOpenModuleModal={handleOpenModuleModal}
        />
      )}

      {showModuleModal && moduleId && (
        <ModuleModal
          showModuleModal={showModuleModal}
          moduleId={moduleId}
          handleCloseModuleModal={handleCloseModuleModal}
          handleOpenItemModal={handleOpenItemModal}
          setSurveyModal={setShowSurveyModal}
          setModuleModal={setShowModuleModal}
          setSurveyId={setSurveyId}
        />
      )}

      {showItemModal && itemId && (
        <ItemModal
          showItemModal={showItemModal}
          itemId={itemId}
          itemIndex={itemIndex}
          setItemIndex={setItemIndex}
          items={items}
          handleCloseItemModal={handleCloseItemModal}
          setItemModal={setShowItemModal}
          setModuleModal={setShowModuleModal}
          setSurveyModal={setShowSurveyModal}
          setItemId={setItemId}
        />
      )}
    </>
  );
}
